import { useState, useCallback } from "react";
import { ScanLine, Upload, Download, Trash2, FileText, Loader2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "What is PDF OCR?",
    answer: "OCR (Optical Character Recognition) extracts text from scanned PDFs or image-based documents, making the text searchable and copyable."
  },
  {
    question: "Is this OCR tool free?",
    answer: "Yes, completely free with no signup. Extract text from unlimited scanned PDFs."
  },
  {
    question: "What languages are supported?",
    answer: "Currently English is supported. The OCR engine is optimized for English text recognition."
  },
  {
    question: "How accurate is the OCR?",
    answer: "Our OCR achieves high accuracy on clear scans. Quality depends on scan resolution and document clarity."
  },
  {
    question: "Can I OCR a regular PDF with text?",
    answer: "Yes, for PDFs with selectable text, we extract it directly. For scanned/image PDFs, we use OCR technology."
  }
];

const languages = [
  { code: "eng", name: "English" },
];

const PdfOcrPage = () => {
  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState("eng");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const loadPreview = async (pdfFile) => {
    setIsLoadingPreview(true);
    const formData = new FormData();
    formData.append("file", pdfFile);

    try {
      const response = await axios.post(`${API}/pdf-preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setPreview(response.data);
      }
    } catch (error) {
      console.error("Preview error:", error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResult(null);
      setPreview(null);
      await loadPreview(droppedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResult(null);
      setPreview(null);
      await loadPreview(selectedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const handleExtract = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("language", language);

    try {
      const response = await axios.post(`${API}/pdf-ocr`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "OCR extraction failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        const downloadUrl = `${API}/download/${result.files[0].id}`;
        await downloadFile(downloadUrl, result.files[0].name || "ocr-result.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setPreview(null);
  };

  return (
    <>
      <SEOHead
        title="PDF OCR Free Online | Extract Text from Scanned PDF"
        description="Extract text from scanned PDFs using OCR technology. Free online OCR tool. Convert image-based PDFs to searchable text. No signup required."
        canonicalPath="/pdf-ocr"
        keywords="PDF OCR, extract text from PDF, scanned PDF to text, OCR free online, PDF text extraction"
      />
      
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#FF3B30] flex items-center justify-center">
                <ScanLine className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                PDF OCR Free Online
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Extract text from scanned PDFs and image-based documents using advanced OCR technology. 
              Make your documents searchable and copyable. No signup required.
            </p>
          </div>

        {/* Upload Area */}
        {!result && (
          <div className="space-y-6 animate-fade-in-up animate-delay-100">
            <div
              className={`upload-zone ${isDragging ? "dragging" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input").click()}
              data-testid="upload-zone"
            >
              <input
                id="file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {file ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-[#FF3B30]" />
                  </div>
                  <p className="font-medium text-lg">{file.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                    {preview && ` • ${preview.total_pages} pages`}
                  </p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">Drop your PDF here</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Works with scanned documents and image-based PDFs
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Preview Section */}
            {isLoadingPreview && (
              <div className="bg-card border border-border p-6 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#FF3B30]" />
                <p className="text-sm text-muted-foreground">Loading preview...</p>
              </div>
            )}

            {preview && !isLoadingPreview && (
              <div className="bg-card border border-border p-4" data-testid="preview-section">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-4 h-4 text-[#FF3B30]" />
                  <span className="text-sm font-medium">Preview ({preview.total_pages} pages to scan)</span>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {preview.pages.slice(0, 8).map((page, index) => (
                    <div key={index} className="relative aspect-[3/4] bg-secondary overflow-hidden border border-border">
                      <img
                        src={`${API}/download/${preview.preview_id}/${page.thumbnail.split('/').pop()}`}
                        alt={`PDF page ${page.page} thumbnail for OCR text extraction`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs text-center py-0.5">
                        {page.page}
                      </span>
                    </div>
                  ))}
                  {preview.total_pages > 8 && (
                    <div className="aspect-[3/4] bg-secondary flex items-center justify-center border border-border">
                      <span className="text-sm text-muted-foreground">+{preview.total_pages - 8}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="bg-secondary p-4 rounded-sm">
              <p className="text-sm text-muted-foreground">
                <strong>How it works:</strong> The OCR engine will extract text from each page.
                For scanned documents, it uses image recognition. For digital PDFs, it extracts
                text directly for better accuracy.
              </p>
            </div>

            {/* Language Selection */}
            <div className="border border-border p-4">
              <Label className="mb-2 block">Document Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                Select the primary language of your document for better OCR accuracy
              </p>
            </div>

            <Button
              onClick={handleExtract}
              disabled={!file || isProcessing}
              className="w-full h-14 bg-black hover:bg-black/90 text-white rounded-sm btn-hover-lift text-lg font-medium"
              data-testid="extract-button"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Extracting Text...
                </>
              ) : (
                <>
                  <ScanLine className="w-5 h-5 mr-2" />
                  Extract Text (OCR)
                </>
              )}
            </Button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Extracted Text</h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(result.text_preview);
                      toast.success("Text copied to clipboard");
                    }}
                    className="rounded-sm"
                  >
                    Copy Preview
                  </Button>
                </div>
              </div>
              <div className="bg-secondary p-4 max-h-96 overflow-y-auto font-mono text-sm whitespace-pre-wrap">
                {result.text_preview}
              </div>
            </div>

            <div className="flex justify-center gap-4">
              <Button
                variant="outline"
                onClick={handleReset}
                className="rounded-sm"
                data-testid="reset-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Extract Another
              </Button>
              <Button
                onClick={handleDownload}
                className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                data-testid="download-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Full Text (.txt)
              </Button>
            </div>

            <div className="bg-secondary p-4 text-center">
              <p className="text-sm text-muted-foreground">
                ⏱ File will be automatically deleted in 30 minutes
              </p>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <ToolContentSection toolKey="pdf-ocr" content={toolContent["pdf-ocr"]} />
        <FAQSection faqs={faqs} />

        {/* Related Tools */}
        <RelatedTools currentTool="pdf-ocr" />
      </div>
    </div>
    </>
  );
};

export default PdfOcrPage;
